import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api, { BACKEND_URL } from '../lib/api';
import { Check, Clock, Shield, GanttChart } from 'lucide-react';
import BexcodeLogo from '../components/BexcodeLogo';

function googleLogin() {
  window.location.href = `${BACKEND_URL}/api/auth/google`;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('login'); // 'login' or 'forgot'
  const [forgotEmail, setForgotEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [ok, setOk] = useState(false);
  const [link, setLink] = useState(null);

  const submit = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await login(email, password);
      nav('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg('');
    setLink(null);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: forgotEmail });
      setOk(true);
      if (data.emailed) {
        setMsg('Reset link sent to your email. It expires in 1 hour.');
      } else {
        setMsg('Reset link generated (email service not configured — use this link):');
        setLink(data.reset_link);
      }
    } catch (err) {
      setOk(false);
      setMsg(err.response?.data?.error || err.message || 'Failed to request password reset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--trello-card-bg)', fontFamily: "'Inter', sans-serif" }}>
      {/* Left Pane (Dark Gradient) - Hidden on mobile */}
      <div style={{
        flex: '0 0 45%',
        background: 'linear-gradient(135deg, #021a30 0%, #0d0d0d 100%)',
        padding: '60px 50px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: '#fff',
        position: 'relative',
        boxSizing: 'border-box'
      }} className="hidden md:flex">

        {/* Logo Header */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BexcodeLogo size={32} textColor="#fff" />
        </div>

        {/* Feature Highlights */}
        <div style={{ margin: 'auto 0', maxWidth: 440 }}>
          <h2 style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.25, color: '#fff', marginBottom: 40, fontFamily: "'Outfit', sans-serif" }}>
            Plan sprints, ship work, and keep every team aligned.
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Feature 1 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <GanttChart size={18} style={{ color: 'rgba(255,255,255,0.8)' }} />
              </div>
              <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 14, fontWeight: 500 }}>
                Kanban boards, sprints, and real-time reporting
              </span>
            </div>

            {/* Feature 2 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Clock size={18} style={{ color: 'rgba(255,255,255,0.8)' }} />
              </div>
              <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 14, fontWeight: 500 }}>
                Built-in time tracking and client-ready timesheets
              </span>
            </div>

            {/* Feature 3 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255, 255, 255, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Shield size={18} style={{ color: 'rgba(255,255,255,0.8)' }} />
              </div>
              <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 14, fontWeight: 500 }}>
                Role-based permissions and a full audit log
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 11, fontWeight: 500 }}>
          Powered by Bexcode Pvt. Ltd.
        </div>
      </div>

      {/* Right Pane (White Background, Centered Form) */}
      <div style={{
        flex: 1,
        background: 'var(--trello-card-bg)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 24px',
        boxSizing: 'border-box'
      }}>
        {view === 'forgot' ? (
          /* Forgot Password View */
          <div style={{ width: '100%', maxWidth: 380 }} data-testid="forgot-password-form">
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                Forgot Password
              </h1>
              <p style={{ color: '#64748b', marginTop: 8, fontSize: 14, margin: '8px 0 0 0' }}>
                Enter your email to request a password reset
              </p>
            </div>

            <form onSubmit={handleForgotPasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#f0f7ff',
                    border: '1px solid #b9ddfc',
                    borderRadius: 8,
                    padding: '12px 16px',
                    fontSize: 14,
                    color: '#0f172a',
                    outline: 'none',
                    transition: 'border-color 0.15s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0079bf'}
                  onBlur={(e) => e.target.style.borderColor = '#b9ddfc'}
                />
              </div>

              {msg && <div style={{ color: ok ? '#61BD4F' : '#DE350B', fontSize: 13, fontWeight: 500 }} data-testid="forgot-message">{msg}</div>}
              {link && (
                <div style={{ fontSize: 12, background: 'var(--trello-list-bg)', border: '1px solid #F2D600', padding: 8, borderRadius: 4, wordBreak: 'break-all' }} data-testid="forgot-link">
                  <a href={link} style={{ color: '#026AA7' }}>{link}</a>
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                style={{
                  width: '100%',
                  background: '#0079bf',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontWeight: 700,
                  fontSize: 14,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(0, 121, 191, 0.25)'
                }}
                onMouseEnter={(e) => e.target.style.background = '#026aa7'}
                onMouseLeave={(e) => e.target.style.background = '#0079bf'}
              >
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#64748b' }}>
              <button
                type="button"
                onClick={() => { setView('login'); setMsg(''); setLink(null); }}
                style={{
                  color: '#0079bf',
                  fontWeight: 600,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Back to log in
              </button>
            </p>
          </div>
        ) : (
          /* Login View */
          <div style={{ width: '100%', maxWidth: 380 }} data-testid="login-form">
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: 32, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif" }}>
                Welcome back
              </h1>
              <p style={{ color: '#64748b', marginTop: 8, fontSize: 14, margin: '8px 0 0 0' }}>
                Sign in to your BexConnect workspace
              </p>
            </div>

            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#f0f7ff',
                    border: '1px solid #b9ddfc',
                    borderRadius: 8,
                    padding: '12px 16px',
                    fontSize: 14,
                    color: '#0f172a',
                    outline: 'none',
                    transition: 'border-color 0.15s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0079bf'}
                  onBlur={(e) => e.target.style.borderColor = '#b9ddfc'}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', margin: 0 }}>Password</label>
                  <button
                    type="button"
                    data-testid="login-forgot-password-link"
                    onClick={() => setView('forgot')}
                    style={{
                      fontSize: 12,
                      color: '#0079bf',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      fontWeight: 600,
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#f0f7ff',
                    border: '1px solid #b9ddfc',
                    borderRadius: 8,
                    padding: '12px 16px',
                    fontSize: 14,
                    color: '#0f172a',
                    outline: 'none',
                    transition: 'border-color 0.15s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0079bf'}
                  onBlur={(e) => e.target.style.borderColor = '#b9ddfc'}
                />
              </div>

              {error && <div data-testid="login-error" style={{ color: '#DE350B', fontSize: 13, fontWeight: 500 }}>{error}</div>}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={busy}
                style={{
                  width: '100%',
                  background: '#0079bf',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontWeight: 700,
                  fontSize: 14,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(0, 121, 191, 0.25)'
                }}
                onMouseEnter={(e) => e.target.style.background = '#026aa7'}
                onMouseLeave={(e) => e.target.style.background = '#0079bf'}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: '#cbd5e1' }} />
            </div>

            <button
              data-testid="login-google"
              onClick={googleLogin}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1px solid #cbd5e1',
                background: 'var(--trello-card-bg)',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontWeight: 600,
                fontSize: 14,
                color: '#334155',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
              onMouseLeave={(e) => e.target.style.background = '#fff'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" /></svg>
              Continue with Google
            </button>

            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#94a3b8', lineHeight: 1.4 }}>
              Already belong to more than one organization? Sign in once and switch from the top bar.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
